import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AlertService } from "@modules/alert";
import * as moment from "moment";
import { finalize } from "rxjs";
import {
  AiEntityType,
  AiTokensApiService,
} from "../../services/ai-tokens-api.service";
import { ItemsListComponent } from "../../../../components/dashboard/items-list/items-list.component";
import { AiUsageFilterOptions, AiUsageRow } from "../../models/ai-usage.model";

interface UsageTotals {
  costCents: number;
  tokens: number;
  calls: number;
}

const EMPTY_TOTALS: UsageTotals = {
  costCents: 0,
  tokens: 0,
  calls: 0,
};

/**
 * The filter keys, doubling as the upstream query param names. Listed here
 * rather than derived from `availableFilters` because a stale selection has to
 * be detectable even after its whole filter drops out of the options.
 */
const FILTER_IDS = ["userIds", "features", "modelIds"];

@Component({
  selector: "app-ai-usage-list",
  templateUrl: "./ai-usage-list.component.html",
  styleUrls: ["./ai-usage-list.component.scss"],
})
export class AiUsageListComponent
  extends ItemsListComponent
  implements OnInit, OnDestroy
{
  @Input() entity_id!: string;
  @Input() entity_type!: AiEntityType;

  totals: UsageTotals = { ...EMPTY_TOTALS };
  initialDateRange!: { start_date: any; end_date: any };

  /**
   * Held separately from the base class's `request_call`, which tracks the row
   * request. The two are in flight at the same time on every date change, so
   * sharing one handle would cancel the wrong one.
   */
  private filters_call;

  /**
   * Narrowed from the base class's untyped `items`. Without this the template's
   * `row` is `any`, so a field name that does not match what xpath-tokens
   * returns compiles cleanly and only surfaces as blank cells at runtime —
   * which is precisely the failure this table's verification depends on catching.
   */
  override items: AiUsageRow[] = [];

  constructor(
    protected service: AiTokensApiService,
    protected alertService: AlertService
  ) {
    super();
  }

  ngOnInit() {
    // Seeded before the first render so the template never has to create these.
    // filterValue() is bound to [ngModel] and therefore runs on every
    // change-detection pass; allocating there would both churn the model and
    // mutate state mid-check.
    FILTER_IDS.forEach((id) => (this.filters[id] = []));

    const start = moment().startOf("month");
    const end = moment();
    this.startDate = start.toDate();
    this.endDate = end.toDate();
    this.initialDateRange = { start_date: start, end_date: end };
    this.getFilterOptions();
    this.getItems(1);
  }

  public getItems(page: number = 1) {
    if (!this.entity_id || !this.entity_type) return;
    if (this.request_call) this.request_call.unsubscribe();

    const base = this.getParams(page);
    const upstreamParams = {
      page: base["page"],
      perPage: base["per_page"],
      startDate: base["start_date"],
      endDate: base["end_date"],
      // Empty collapses to undefined rather than `[]`: an empty array would be
      // serialised as no params at all anyway, but going through undefined
      // keeps "selected then cleared" and "never selected" the same request.
      features: this.selected("features"),
      modelIds: this.selected("modelIds"),
      userIds: this.selected("userIds"),
    };

    this.isLoadingGeneral = true;
    this.request_call = this.service
      .listUsage(this.entity_type, this.entity_id, upstreamParams)
      .pipe(finalize(() => (this.isLoadingGeneral = false)))
      .subscribe(
        (data) => this.processResponse(data),
        (error) => this.reportError(error)
      );
  }

  protected processResponse(data: any) {
    this.items = data?.items ?? [];
    this.p = data?.currentPage ?? 1;
    this.total = data?.total ?? 0;
    this.itemsPage = data?.perPage ?? this.perPage;
    this.isLoadingGeneral = false;
    // Field-MERGE rather than whole-object fallback: a tokens deployment that
    // predates XPT-12046 returns `totals` without the cache fields, and `??`
    // would leave them undefined (rendering a blank cell, not $0.00). Merging
    // guarantees every key is present and numeric whatever the API sends.
    this.totals = { ...EMPTY_TOTALS, ...(data?.totals ?? {}) };
  }

  /**
   * Share of the prompt served from cache, as a whole percent. `cacheReadTokens`
   * is a subset of `inputTokens`, so the ratio is already meaningful without any
   * API change. Returns null in two cases, both of which the template renders
   * as an em dash rather than a number: `cacheReadTokens == null` means this
   * call never reported cache usage (the `!=`/`==` form also catches
   * `undefined` from an older API); `!row.inputTokens` guards the division so
   * a measured row with zero input tokens renders `—` instead of `NaN%`.
   */
  public hitPercent(row: AiUsageRow): number | null {
    if (row.cacheReadTokens == null || !row.inputTokens) return null;
    return Math.round((row.cacheReadTokens / row.inputTokens) * 100);
  }

  /**
   * Load the values the filter dropdowns can offer.
   *
   * Scoped to the same date window as the rows, so it has to re-run whenever
   * that window moves: a feature only used in March must not stay on offer once
   * the range is switched to April, or picking it would return nothing.
   */
  private getFilterOptions() {
    if (!this.entity_id || !this.entity_type) return;

    // Cancelled for the same reason getItems() cancels its own request, but the
    // consequence here is worse than a stale render: two quick date changes can
    // resolve out of order, and the older response would then run
    // dropStaleFilterValues() against the newer window and silently null a
    // selection that is perfectly valid for it.
    if (this.filters_call) this.filters_call.unsubscribe();

    // Clearing the date inside the filters panel sets both bounds to the STRING
    // "null" rather than a real null — the convention getParams() also has to
    // special-case. It is truthy, so a plain presence check would push it into
    // formatDataParam() and send `Invalid date` upstream.
    const hasRange =
      this.startDate &&
      this.endDate &&
      this.startDate !== "null" &&
      this.endDate !== "null";

    this.filters_call = this.service
      .listUsageFilters(this.entity_type, this.entity_id, {
        startDate: hasRange ? this.formatDataParam(this.startDate) : undefined,
        endDate: hasRange ? this.formatDataParam(this.endDate) : undefined,
      })
      .subscribe(
        (options) => this.setAvailableFilters(options),
        (error) => {
          // Leave the table usable rather than blocking on the dropdowns: the
          // rows are the point, the filters are an aid to reading them.
          this.availableFilters = [];
          this.reportError(error);
        }
      );
  }

  /**
   * All three filters are always rendered, including ones with nothing to
   * offer. The User dropdown is empty for any account whose AI activity all
   * predates per-user attribution, and hiding it there would read as the
   * feature being missing rather than as that account having no attributed
   * calls yet; the disabled "no items found" option says which it is.
   */
  private setAvailableFilters(options: AiUsageFilterOptions) {
    // Ordered who → what → which model: "whose spend is this" is the question
    // this tab gets opened for, so User leads.
    this.availableFilters = [
      { id: "userIds", label: "User", data: options?.users ?? [] },
      {
        id: "features",
        label: "Feature",
        data: (options?.features ?? []).map((feature) => ({
          id: feature,
          text: feature,
        })),
      },
      {
        id: "modelIds",
        label: "Model",
        // Keyed on the numeric id, not the identifier: the same identifier can
        // exist under two providers, and the same model can be re-registered
        // with new pricing.
        data: (options?.models ?? []).map((model) => ({
          id: model.id,
          text: `${model.provider}/${model.modelIdentifier}`,
        })),
      },
    ];

    if (this.dropStaleFilterValues()) {
      this.getItems(1);
    }
  }

  /**
   * Clear any selection the refreshed options no longer offer, reporting
   * whether anything was cleared.
   *
   * Moving the date range can drop a feature, model or user out of the option
   * set entirely. Without this the selection survives in `filters` while
   * disappearing from the dropdown — filtering the table down to nothing with
   * no visible control left to undo it.
   */
  private dropStaleFilterValues(): boolean {
    let changed = false;

    for (const id of FILTER_IDS) {
      const active = this.filterValue(id);
      if (!active.length) continue;

      // A filter that vanished from availableFilters altogether has no options
      // array to match against, which makes every surviving selection stale by
      // definition — hence the `?? []` rather than skipping missing ids.
      const options = (this.availableFilters ?? []).find((f) => f.id === id);
      // Pruned value by value, not all-or-nothing: with multi-select, a date
      // change can invalidate one chip out of four, and dropping the other
      // three with it would silently widen the table the user was narrowing.
      const kept = active.filter((value) =>
        (options?.data ?? []).some((option) => option.id === value)
      );

      if (kept.length !== active.length) {
        this.filters[id] = kept;
        changed = true;
      }
    }

    return changed;
  }

  /**
   * Empty the named filters. Each select reads its selection back through
   * filterValue(), so clearing here is what empties the control on screen.
   */
  private clearFilterValues(ids: string[]) {
    ids.forEach((id) => (this.filters[id] = []));
  }

  /**
   * Whether any of the three dropdowns currently narrows the table. Drives the
   * empty state and the clear-all control: combining filters CAN legitimately
   * match nothing (feature X plus a user who never used it), and without this
   * distinction that case is indistinguishable from "this account has no AI
   * usage at all".
   */
  get hasActiveFilters(): boolean {
    return FILTER_IDS.some((id) => this.filterValue(id).length > 0);
  }

  public clearAllFilters() {
    this.clearFilterValues(FILTER_IDS);
    this.getItems(1);
  }

  /**
   * Current selection of one dropdown, always an array. Normalised because a
   * multi-select can write `null` rather than `[]` once the last value is
   * removed, and handing that straight back as the model breaks the next
   * change.
   *
   * Returns the STORED array rather than a fresh one. This is bound to
   * [ngModel], so it is re-evaluated on every change-detection pass; handing
   * back a new `[]` each time would look like a new value every pass and spin
   * change detection indefinitely.
   */
  public filterValue(id: string): any[] {
    if (!Array.isArray(this.filters[id])) this.filters[id] = [];

    return this.filters[id];
  }

  /** The same selection, or undefined when nothing is chosen. */
  private selected(id: string): any[] | undefined {
    const value = this.filterValue(id);

    return value.length ? value : undefined;
  }

  public setFilter(id: string, value: any) {
    this.filters[id] = Array.isArray(value) ? value : [];
    // Back to page 1: the current page number almost certainly does not exist
    // in the narrowed result set, and landing past the end shows an empty table
    // that looks like the filter matched nothing.
    this.getItems(1);
  }

  /**
   * Both halves matter: setDate() stores the range and reloads the rows, and
   * the options have to be refetched because which features, models and users
   * occur depends on the window.
   */
  public setDateRange(event: any) {
    this.setDate(event);
    this.getFilterOptions();
  }

  /**
   * Surface a failure whatever shape it arrives in.
   *
   * Two error formats reach this tab. The gateway validates locally and returns
   * Laravel's `{errors: {...}}`, but it also RELAYS failures from xpath-tokens,
   * which is Nest and returns `{message: string | string[]}`. Handling only the
   * first meant an upstream rejection produced no alert at all — the table just
   * stopped updating with nothing on screen to say why.
   */
  private reportError(error: any) {
    const errors = error?.error?.errors;
    if (errors) {
      this.alertService.errors(errors);

      return;
    }

    const message = error?.error?.message;
    if (message) {
      this.alertService.errors(Array.isArray(message) ? message : [message]);
    }
  }

  public navigateByUrl(page: number = 1) {
    this.getItems(page);
  }

  /**
   * Both requests are cancelled, not just the rows one. Navigating away while
   * the options call is in flight would otherwise land setAvailableFilters() —
   * and the getItems() it can trigger — on a destroyed component.
   */
  ngOnDestroy() {
    if (this.request_call) this.request_call.unsubscribe();
    if (this.filters_call) this.filters_call.unsubscribe();
  }
}
